import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "./api";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Run an async API call on mount (and whenever `deps` change), tracking
 * loading/error/data. A 401 redirects to /login. Returns a `reload` fn.
 *
 * The fetcher receives an AbortSignal so an in-flight request is cancelled when
 * deps change or the component unmounts.
 */
export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
): AsyncState<T> & { reload: () => void } {
  const navigate = useNavigate();
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const ctrl = new AbortController();
    // Show the loading state only on the INITIAL load (no data yet). On a reload
    // or dep change we keep the current data on screen and refetch in the
    // background, then swap — so a mutation never blanks the whole view to a
    // spinner ("don't reload the whole page when items are updated"). Components
    // that must fully reset on a key change use `key=…` to remount instead.
    setState((s) => ({ ...s, loading: s.data === null, error: null }));
    fetcherRef
      .current(ctrl.signal)
      .then((data) => {
        if (!ctrl.signal.aborted) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        if (err instanceof ApiError && err.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        const message = err instanceof Error ? err.message : "Unexpected error";
        setState({ data: null, loading: false, error: message });
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/**
 * Helper for one-shot mutations (create/update/move/etc.) with consistent 401
 * handling and pending/error state.
 */
export function useMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          navigate("/login", { replace: true });
          return undefined;
        }
        setError(err instanceof Error ? err.message : "Unexpected error");
        return undefined;
      } finally {
        setPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fn],
  );

  return { run, pending, error, setError };
}
